import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Radio, Users, Plus, Loader2, Crown, Trophy, PlayCircle } from "lucide-react";
import StreakFire from "@/components/plyx/StreakFire";
import LiveHourBadge from "@/components/plyx/LiveHourBadge";

/**
 * Plyx Live discovery page — `/plyx/live`
 *
 * Public. Shows the active live streams grid + a "Go Live" CTA for logged-in
 * Plyx creators. Polls every 15s so newly-started streams pop in.
 */
const PlyxLive = () => {
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [topGifters, setTopGifters] = useState([]);
  const [recordings, setRecordings] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchStreams = () => {
      api.get("/plyx/live/active").then((r) => {
        if (!mounted) return;
        setStreams(r.data?.streams || []);
        setEnabled(r.data?.enabled !== false);
      }).catch(() => {}).finally(() => mounted && setLoading(false));
    };
    fetchStreams();
    const t = setInterval(fetchStreams, 15000);
    // One-shot loads for spotlight + replays.
    api.get("/plyx/top-live-gifters?window_hours=24&limit=5")
      .then((r) => mounted && setTopGifters(r.data?.top_gifters || []))
      .catch(() => {});
    api.get("/plyx/recordings?limit=6")
      .then((r) => mounted && setRecordings(r.data?.recordings || []))
      .catch(() => {});
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-20">
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-[#FF3D7F] via-[#FFCC2E] to-[#00E0C7] bg-clip-text text-transparent">
              Plyx Live
            </h1>
            <p className="text-white/60 mt-2 text-sm">
              Real-time broadcasts from creators across Africa. Tap any card to drop in.
            </p>
          </div>
          {user && (
            <button
              data-testid="plyx-live-go-live-btn"
              onClick={() => navigate("/plyx/live/new")}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-bold text-sm bg-gradient-to-r from-[#FF3D7F] to-[#FFCC2E] text-black hover:scale-105 transition-transform shadow-[0_8px_24px_-8px_rgba(255,61,127,0.6)]"
            >
              <Radio className="w-4 h-4" /> Go Live
            </button>
          )}
        </div>

        {!enabled && (
          <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/5 p-6 text-yellow-200 text-sm">
            Live streaming is currently disabled. Check back soon.
          </div>
        )}

        <LiveHourBadge />

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : streams.length === 0 ? (
          <div data-testid="plyx-live-empty" className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <Radio className="w-10 h-10 mx-auto text-white/30 mb-3" />
            <p className="text-white/70 font-medium">No-one&apos;s live right now.</p>
            <p className="text-white/40 text-sm mt-1">
              {user ? "Be the first — tap Go Live above." : "Sign in to broadcast."}
            </p>
            {!user && (
              <Link to="/plyx/login" className="inline-block mt-4 text-[#FFCC2E] text-sm font-semibold hover:underline">
                Sign in →
              </Link>
            )}
          </div>
        ) : (
          <div data-testid="plyx-live-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {streams.map((s) => (
              <Link
                key={s.room_name}
                to={`/plyx/live/${s.room_name}`}
                data-testid={`plyx-live-card-${s.room_name}`}
                className={`group relative rounded-2xl overflow-hidden border bg-gradient-to-br from-[#FF3D7F]/15 via-[#0a0a0f] to-[#00E0C7]/15 aspect-[9/14] flex flex-col justify-end p-4 hover:scale-[1.02] transition-transform ${s.is_finals ? "border-[#FFD700] ring-2 ring-[#FFD700]/40" : "border-white/10"}`}
              >
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-[10px] font-black tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                </div>
                {s.is_finals && (
                  <div data-testid={`plyx-live-finals-${s.room_name}`} className="absolute top-3 left-20 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFB42D] text-black text-[10px] font-black tracking-wider">
                    🏆 FINALS
                  </div>
                )}
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-white/80 text-[11px]">
                  <Users className="w-3 h-3" /> {s.viewer_count || 0}
                </div>
                {s.mini_hour?.active && (
                  <div
                    data-testid={`plyx-live-mini-active-${s.room_name}`}
                    className="absolute top-12 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-[#FFCC2E] via-[#FF9E2D] to-[#FF3D7F] text-black text-[10px] font-black tracking-wide shadow-lg"
                    title="A Mini Hour 2× boost is live in this stream right now"
                  >
                    ⚡ 2× MINI HOUR
                  </div>
                )}
                {!s.mini_hour?.active && s.mini_hour?.available && (
                  <div
                    data-testid={`plyx-live-mini-loaded-${s.room_name}`}
                    className="absolute top-12 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFCC2E]/15 border border-[#FFCC2E]/30 text-[#FFCC2E] text-[10px] font-bold tracking-wide"
                    title="This stream still has a Mini Hour boost to spend"
                  >
                    ⚡ Boost loaded
                  </div>
                )}
                <div className="relative z-10">
                  <p className="text-white font-bold text-base line-clamp-2">{s.title}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <p className="text-white/70 text-xs">
                      @{s.creator?.handle || "creator"}
                      {s.creator?.verified && <span className="ml-1 text-white/85">✓</span>}
                    </p>
                    {s.creator?.live_streak_count > 0 && (
                      <StreakFire streak={s.creator.live_streak_count} best={s.creator.live_streak_best} />
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Top live gifters (last 24h) */}
        {topGifters.length > 0 && (
          <section data-testid="plyx-live-top-gifters" className="mt-12">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-[#FFD700]" />
              <h2 className="font-black text-lg text-white">Top live gifters · last 24h</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {topGifters.map((g) => (
                <Link
                  key={g.handle}
                  to={`/plyx/u/${g.handle}`}
                  data-testid={`plyx-live-top-gifter-${g.handle}`}
                  className="shrink-0 w-44 rounded-2xl border border-[#FFD700]/30 bg-gradient-to-br from-[#FFD700]/10 to-[#FF2D7A]/10 p-4 hover:scale-[1.03] transition-transform"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{g.rank === 1 ? "🥇" : g.rank === 2 ? "🥈" : g.rank === 3 ? "🥉" : `#${g.rank}`}</span>
                    <span className="font-bold text-white text-sm truncate">@{g.handle}</span>
                  </div>
                  <p className="text-[#FFCC2E] text-xs font-mono">{g.coins.toLocaleString()} coins</p>
                  <p className="text-white/40 text-[10px] mt-0.5">{g.count} gift{g.count === 1 ? "" : "s"}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Past Finals replays */}
        {recordings.length > 0 && (
          <section data-testid="plyx-live-recordings" className="mt-12">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-[#FFB42D]" />
              <h2 className="font-black text-lg text-white">Past Finals · replays</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recordings.map((r) => (
                <div
                  key={r.room_name}
                  data-testid={`plyx-live-recording-${r.room_name}`}
                  className="p-3 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#FFB42D]/40 transition-colors"
                >
                  <a
                    href={r.recording_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3"
                  >
                    <PlayCircle className="w-8 h-8 text-[#FFB42D] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-white text-sm truncate">{r.title}</p>
                      <p className="text-white/50 text-xs truncate">@{r.creator?.handle} · {r.ended_at?.slice(0, 10)}</p>
                    </div>
                  </a>
                  {/* Hot-moments timeline — each chip jumps the player into a 30s
                      window centred on the tip-storm. Backend builds the
                      `#t=startSec,endSec` deep link so we just open the URL. */}
                  {Array.isArray(r.hot_moments) && r.hot_moments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`plyx-hot-moments-${r.room_name}`}>
                      <span className="text-[10px] uppercase tracking-wide text-[#FF2D7A] font-bold">🔥 Hot moments</span>
                      {r.hot_moments.slice(0, 5).map((m) => (
                        <button
                          key={m.moment_id}
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const res = await api.get(`/plyx/recordings/${r.room_name}/hot/${m.moment_id}`);
                              if (res.data?.playback_url) {
                                window.open(res.data.playback_url, "_blank", "noopener");
                              }
                            } catch { /* swallow — link still works above */ }
                          }}
                          data-testid={`plyx-hot-moment-${m.moment_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/15 border border-[#FF2D7A]/40 text-[10px] text-white hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/30"
                          title={`Jump to ${m.offset_sec}s · ${m.coins} coins from ${m.tipper_count} viewers`}
                        >
                          <span className="font-mono tabular-nums">
                            {Math.floor(m.offset_sec / 60)}:{String(m.offset_sec % 60).padStart(2, "0")}
                          </span>
                          <span className="text-white/60">·</span>
                          <span className="text-[#FFB42D] font-semibold">{m.coins}🪙</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PlyxLive;
